import { create } from 'zustand';
import {
  PresentationData,
  PresentationSlide,
  TypographySettings,
  AnimationSettings,
  ThemePreset,
  PRESENTATION_CREDITS,
} from '../types';
import { DEFAULT_TYPOGRAPHY, DEFAULT_ANIMATIONS, getThemeById } from '../data/themePresets';
import { useAuthStore } from './auth';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface PresentationState {
  // Current presentation
  currentPresentation: PresentationData | null;
  presentationId: string | null;
  isDirty: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // History for undo/redo
  history: PresentationData[];
  historyIndex: number;
  maxHistoryLength: number;

  // UI state
  selectedSlideIndex: number;
  activePanel: 'themes' | 'typography' | 'animations' | null;
  isPreviewMode: boolean;
  isExporting: boolean;

  // Credit confirmation
  pendingAction: {
    type: 'theme' | 'typography' | 'animations';
    data: unknown;
    credits: number;
  } | null;

  // Actions
  setPresentation: (presentation: PresentationData, id: string) => void;
  loadPresentation: (id: string) => Promise<boolean>;

  // Customization (with credit deduction)
  applyTheme: (themeId: string) => Promise<boolean>;
  applyTypography: (settings: TypographySettings) => Promise<boolean>;
  applyAnimations: (settings: AnimationSettings) => Promise<boolean>;

  // Slide management
  updateSlide: (slideIndex: number, data: Partial<PresentationSlide>) => void;
  selectSlide: (index: number) => void;

  // Slide operations
  duplicateSlide: (index: number) => void;
  deleteSlide: (index: number) => void;
  moveSlide: (fromIndex: number, toIndex: number) => void;
  addSlide: (afterIndex?: number) => void;

  // Undo/Redo
  pushToHistory: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // UI
  setActivePanel: (panel: 'themes' | 'typography' | 'animations' | null) => void;
  setPreviewMode: (preview: boolean) => void;
  setPendingAction: (action: PresentationState['pendingAction']) => void;
  confirmPendingAction: () => Promise<boolean>;
  cancelPendingAction: () => void;

  // Export
  exportJSON: () => string;
  exportHTML: () => string;

  // Reset
  reset: () => void;
  setError: (error: string | null) => void;
}

export const usePresentationStore = create<PresentationState>((set, get) => ({
  // Initial state
  currentPresentation: null,
  presentationId: null,
  isDirty: false,
  isLoading: false,
  isSaving: false,
  error: null,
  history: [],
  historyIndex: -1,
  maxHistoryLength: 50,
  selectedSlideIndex: 0,
  activePanel: null,
  isPreviewMode: false,
  isExporting: false,
  pendingAction: null,

  setPresentation: (presentation, id) => {
    // Ensure presentation has customization defaults
    const withDefaults: PresentationData = {
      ...presentation,
      customization: presentation.customization || {
        typography: DEFAULT_TYPOGRAPHY,
        animations: DEFAULT_ANIMATIONS,
      },
    };
    set({
      currentPresentation: withDefaults,
      presentationId: id,
      isDirty: false,
      selectedSlideIndex: 0,
      error: null,
    });
  },

  loadPresentation: async (id) => {
    const token = useAuthStore.getState().token;
    if (!token) return false;

    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_URL}/presentations/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load presentation');
      }

      const result = await response.json() as { success: boolean; data?: { data: PresentationData } };
      if (result.success && result.data) {
        const presentation = result.data.data;
        const withDefaults: PresentationData = {
          ...presentation,
          customization: presentation.customization || {
            typography: DEFAULT_TYPOGRAPHY,
            animations: DEFAULT_ANIMATIONS,
          },
        };
        set({
          currentPresentation: withDefaults,
          presentationId: id,
          isLoading: false,
          selectedSlideIndex: 0,
        });
        return true;
      }
      throw new Error('Invalid response');
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load presentation',
      });
      return false;
    }
  },

  applyTheme: async (themeId) => {
    const { currentPresentation, presentationId } = get();
    const token = useAuthStore.getState().token;
    if (!currentPresentation || !presentationId || !token) return false;

    const theme = getThemeById(themeId);
    if (!theme) return false;

    set({ isSaving: true, error: null });

    try {
      const response = await fetch(`${API_URL}/presentations/${presentationId}/theme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ themeId }),
      });

      const result = await response.json() as {
        success: boolean;
        data?: { presentation: PresentationData; credits_remaining: number };
        error?: string;
      };

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to apply theme');
      }

      // Update credits in auth store
      if (result.data?.credits_remaining !== undefined) {
        useAuthStore.getState().updateCredits(result.data.credits_remaining);
      }

      // Update presentation with new theme
      const updatedPresentation: PresentationData = {
        ...currentPresentation,
        theme: theme.category as PresentationData['theme'],
        customization: {
          ...currentPresentation.customization!,
          themePreset: themeId,
          typography: {
            ...(currentPresentation.customization?.typography || DEFAULT_TYPOGRAPHY),
            headingFont: theme.fonts.heading,
            bodyFont: theme.fonts.body,
          },
          customColors: theme.colors,
        },
      };

      set({
        currentPresentation: updatedPresentation,
        isSaving: false,
        pendingAction: null,
      });

      return true;
    } catch (error) {
      set({
        isSaving: false,
        error: error instanceof Error ? error.message : 'Failed to apply theme',
      });
      return false;
    }
  },

  applyTypography: async (settings) => {
    const { currentPresentation, presentationId } = get();
    const token = useAuthStore.getState().token;
    if (!currentPresentation || !presentationId || !token) return false;

    set({ isSaving: true, error: null });

    try {
      const response = await fetch(`${API_URL}/presentations/${presentationId}/typography`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ typography: settings }),
      });

      const result = await response.json() as {
        success: boolean;
        data?: { credits_remaining: number };
        error?: string;
      };

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to apply typography');
      }

      // Update credits in auth store
      if (result.data?.credits_remaining !== undefined) {
        useAuthStore.getState().updateCredits(result.data.credits_remaining);
      }

      // Update presentation with new typography
      const updatedPresentation: PresentationData = {
        ...currentPresentation,
        customization: {
          ...currentPresentation.customization!,
          typography: settings,
        },
      };

      set({
        currentPresentation: updatedPresentation,
        isSaving: false,
        pendingAction: null,
      });

      return true;
    } catch (error) {
      set({
        isSaving: false,
        error: error instanceof Error ? error.message : 'Failed to apply typography',
      });
      return false;
    }
  },

  applyAnimations: async (settings) => {
    const { currentPresentation, presentationId } = get();
    const token = useAuthStore.getState().token;
    if (!currentPresentation || !presentationId || !token) return false;

    set({ isSaving: true, error: null });

    try {
      const response = await fetch(`${API_URL}/presentations/${presentationId}/animations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ animations: settings }),
      });

      const result = await response.json() as {
        success: boolean;
        data?: { credits_remaining: number };
        error?: string;
      };

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to apply animations');
      }

      // Update credits in auth store
      if (result.data?.credits_remaining !== undefined) {
        useAuthStore.getState().updateCredits(result.data.credits_remaining);
      }

      // Update presentation with new animations
      const updatedPresentation: PresentationData = {
        ...currentPresentation,
        customization: {
          ...currentPresentation.customization!,
          animations: settings,
        },
      };

      set({
        currentPresentation: updatedPresentation,
        isSaving: false,
        pendingAction: null,
      });

      return true;
    } catch (error) {
      set({
        isSaving: false,
        error: error instanceof Error ? error.message : 'Failed to apply animations',
      });
      return false;
    }
  },

  updateSlide: (slideIndex, data) => {
    const { currentPresentation } = get();
    if (!currentPresentation) return;

    const updatedSlides = [...currentPresentation.slides];
    updatedSlides[slideIndex] = { ...updatedSlides[slideIndex], ...data };

    set({
      currentPresentation: {
        ...currentPresentation,
        slides: updatedSlides,
      },
      isDirty: true,
    });
  },

  selectSlide: (index) => {
    set({ selectedSlideIndex: index });
  },

  // Slide operations
  duplicateSlide: (index) => {
    const { currentPresentation, pushToHistory } = get();
    if (!currentPresentation) return;

    pushToHistory();

    const slides = [...currentPresentation.slides];
    const slideToDuplicate = slides[index];
    const duplicatedSlide: PresentationSlide = {
      ...slideToDuplicate,
      id: `slide-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      number: slides.length + 1,
    };
    slides.splice(index + 1, 0, duplicatedSlide);

    // Renumber slides
    slides.forEach((slide, i) => {
      slide.number = i + 1;
    });

    set({
      currentPresentation: { ...currentPresentation, slides },
      isDirty: true,
      selectedSlideIndex: index + 1,
    });
  },

  deleteSlide: (index) => {
    const { currentPresentation, selectedSlideIndex, pushToHistory } = get();
    if (!currentPresentation || currentPresentation.slides.length <= 1) return;

    pushToHistory();

    const slides = currentPresentation.slides.filter((_, i) => i !== index);

    // Renumber slides
    slides.forEach((slide, i) => {
      slide.number = i + 1;
    });

    // Adjust selected index if needed
    const newSelectedIndex = Math.min(selectedSlideIndex, slides.length - 1);

    set({
      currentPresentation: { ...currentPresentation, slides },
      isDirty: true,
      selectedSlideIndex: newSelectedIndex,
    });
  },

  moveSlide: (fromIndex, toIndex) => {
    const { currentPresentation, pushToHistory } = get();
    if (!currentPresentation) return;
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || fromIndex >= currentPresentation.slides.length) return;
    if (toIndex < 0 || toIndex >= currentPresentation.slides.length) return;

    pushToHistory();

    const slides = [...currentPresentation.slides];
    const [movedSlide] = slides.splice(fromIndex, 1);
    slides.splice(toIndex, 0, movedSlide);

    // Renumber slides
    slides.forEach((slide, i) => {
      slide.number = i + 1;
    });

    set({
      currentPresentation: { ...currentPresentation, slides },
      isDirty: true,
      selectedSlideIndex: toIndex,
    });
  },

  addSlide: (afterIndex) => {
    const { currentPresentation, pushToHistory } = get();
    if (!currentPresentation) return;

    pushToHistory();

    const insertIndex = afterIndex !== undefined ? afterIndex + 1 : currentPresentation.slides.length;
    const newSlide: PresentationSlide = {
      id: `slide-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      number: insertIndex + 1,
      type: 'content',
      title: 'New Slide',
      content: ['Add your content here'],
    };

    const slides = [...currentPresentation.slides];
    slides.splice(insertIndex, 0, newSlide);

    // Renumber slides
    slides.forEach((slide, i) => {
      slide.number = i + 1;
    });

    set({
      currentPresentation: { ...currentPresentation, slides },
      isDirty: true,
      selectedSlideIndex: insertIndex,
    });
  },

  // Undo/Redo
  pushToHistory: () => {
    const { currentPresentation, history, historyIndex, maxHistoryLength } = get();
    if (!currentPresentation) return;

    // Remove any redo states when new changes are made
    const newHistory = history.slice(0, historyIndex + 1);

    // Add current state to history
    newHistory.push(JSON.parse(JSON.stringify(currentPresentation)));

    // Keep history within max length
    if (newHistory.length > maxHistoryLength) {
      newHistory.shift();
    }

    set({
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });
  },

  undo: () => {
    const { history, historyIndex, currentPresentation } = get();
    if (historyIndex < 0 || !currentPresentation) return;

    // Save current state if we're at the end
    let newHistory = [...history];
    let newIndex = historyIndex;

    if (historyIndex === history.length - 1) {
      newHistory.push(JSON.parse(JSON.stringify(currentPresentation)));
    }

    if (historyIndex > 0) {
      newIndex = historyIndex - 1;
      const previousState = newHistory[newIndex];
      set({
        currentPresentation: JSON.parse(JSON.stringify(previousState)),
        history: newHistory,
        historyIndex: newIndex,
        isDirty: true,
      });
    } else if (historyIndex === 0) {
      const previousState = newHistory[0];
      set({
        currentPresentation: JSON.parse(JSON.stringify(previousState)),
        history: newHistory,
        historyIndex: -1,
        isDirty: true,
      });
    }
  },

  redo: () => {
    const { history, historyIndex } = get();
    if (historyIndex >= history.length - 1) return;

    const nextIndex = historyIndex + 1;
    const nextState = history[nextIndex];

    set({
      currentPresentation: JSON.parse(JSON.stringify(nextState)),
      historyIndex: nextIndex,
      isDirty: true,
    });
  },

  canUndo: () => {
    const { historyIndex } = get();
    return historyIndex >= 0;
  },

  canRedo: () => {
    const { history, historyIndex } = get();
    return historyIndex < history.length - 1;
  },

  setActivePanel: (panel) => {
    set({ activePanel: panel });
  },

  setPreviewMode: (preview) => {
    set({ isPreviewMode: preview });
  },

  setPendingAction: (action) => {
    set({ pendingAction: action });
  },

  confirmPendingAction: async () => {
    const { pendingAction, applyTheme, applyTypography, applyAnimations } = get();
    if (!pendingAction) return false;

    switch (pendingAction.type) {
      case 'theme':
        return applyTheme(pendingAction.data as string);
      case 'typography':
        return applyTypography(pendingAction.data as TypographySettings);
      case 'animations':
        return applyAnimations(pendingAction.data as AnimationSettings);
      default:
        return false;
    }
  },

  cancelPendingAction: () => {
    set({ pendingAction: null });
  },

  exportJSON: () => {
    const { currentPresentation } = get();
    if (!currentPresentation) return '';
    return JSON.stringify(currentPresentation, null, 2);
  },

  exportHTML: () => {
    const { currentPresentation } = get();
    if (!currentPresentation) return '';

    const theme = currentPresentation.customization?.customColors || {
      background: '#0f0f1a',
      primary: '#8b5cf6',
      secondary: '#6366f1',
      text: '#ffffff',
      accent: '#a78bfa',
    };

    const typography = currentPresentation.customization?.typography || DEFAULT_TYPOGRAPHY;
    const animations = currentPresentation.customization?.animations || DEFAULT_ANIMATIONS;
    const duration = animations.slideTransitionDuration || 500;
    const slideTransition = animations.slideTransition || 'fade';
    const elementAnimation = animations.elementAnimation || 'fade-in';
    const textAnimation = animations.textAnimation || 'none';

    // Generate standalone HTML presentation
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${currentPresentation.title}</title>
  <link href="https://fonts.googleapis.com/css2?family=${typography.headingFont.replace(' ', '+')}:wght@400;500;600;700;800&family=${typography.bodyFont.replace(' ', '+')}:wght@400;500;600&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: '${typography.bodyFont}', sans-serif;
      background: ${theme.background};
      color: ${theme.text};
      overflow: hidden;
      perspective: 1000px;
    }

    .presentation {
      width: 100vw;
      height: 100vh;
      position: relative;
      transform-style: preserve-3d;
    }

    .slide {
      width: 100%;
      height: 100%;
      padding: 60px 80px;
      display: none;
      flex-direction: column;
      justify-content: center;
      position: absolute;
      top: 0;
      left: 0;
      background: ${theme.background};
      backface-visibility: hidden;
    }

    .slide.active {
      display: flex;
    }

    /* Slide Transition: None */
    .transition-none .slide { transition: none; }
    .transition-none .slide.active { opacity: 1; }
    .transition-none .slide.exit { opacity: 0; display: none; }

    /* Slide Transition: Fade */
    .transition-fade .slide {
      opacity: 0;
      transition: opacity ${duration}ms ease;
    }
    .transition-fade .slide.active { opacity: 1; }

    /* Slide Transition: Slide */
    .transition-slide .slide {
      transform: translateX(100%);
      opacity: 0;
      transition: transform ${duration}ms ease, opacity ${duration}ms ease;
    }
    .transition-slide .slide.active {
      transform: translateX(0);
      opacity: 1;
    }
    .transition-slide .slide.exit {
      transform: translateX(-100%);
      opacity: 0;
    }

    /* Slide Transition: Zoom */
    .transition-zoom .slide {
      transform: scale(0.8);
      opacity: 0;
      transition: transform ${duration}ms ease, opacity ${duration}ms ease;
    }
    .transition-zoom .slide.active {
      transform: scale(1);
      opacity: 1;
    }
    .transition-zoom .slide.exit {
      transform: scale(1.2);
      opacity: 0;
    }

    /* Slide Transition: Flip */
    .transition-flip .slide {
      transform: rotateY(-90deg);
      opacity: 0;
      transition: transform ${duration}ms ease, opacity ${duration}ms ease;
    }
    .transition-flip .slide.active {
      transform: rotateY(0deg);
      opacity: 1;
    }
    .transition-flip .slide.exit {
      transform: rotateY(90deg);
      opacity: 0;
    }

    /* Slide Transition: Cube */
    .transition-cube .slide {
      transform-origin: right center;
      transform: rotateY(-90deg) translateZ(50vw);
      opacity: 0;
      transition: transform ${duration}ms ease, opacity ${duration}ms ease;
    }
    .transition-cube .slide.active {
      transform: rotateY(0deg) translateZ(0);
      opacity: 1;
    }
    .transition-cube .slide.exit {
      transform-origin: left center;
      transform: rotateY(90deg) translateZ(50vw);
      opacity: 0;
    }

    /* Slide Transition: Dissolve */
    .transition-dissolve .slide {
      opacity: 0;
      filter: blur(10px);
      transition: opacity ${duration}ms ease, filter ${duration}ms ease;
    }
    .transition-dissolve .slide.active {
      opacity: 1;
      filter: blur(0);
    }

    .slide-title {
      font-family: '${typography.headingFont}', sans-serif;
      font-size: ${typography.headingSize === 'xlarge' ? '4rem' : typography.headingSize === 'large' ? '3rem' : typography.headingSize === 'medium' ? '2.5rem' : '2rem'};
      font-weight: ${typography.headingWeight === 'extrabold' ? '800' : typography.headingWeight === 'bold' ? '700' : typography.headingWeight === 'medium' ? '500' : '400'};
      color: ${theme.primary};
      margin-bottom: 30px;
    }

    .slide-subtitle {
      font-size: 1.5rem;
      color: ${theme.accent};
      margin-bottom: 20px;
    }

    .slide-content {
      font-size: ${typography.bodySize === 'large' ? '1.5rem' : typography.bodySize === 'medium' ? '1.25rem' : '1rem'};
      line-height: ${typography.lineHeight === 'relaxed' ? '2' : typography.lineHeight === 'normal' ? '1.75' : '1.5'};
    }

    .slide-content ul {
      list-style: none;
      padding-left: 0;
    }

    .slide-content li {
      margin-bottom: 15px;
      padding-left: 30px;
      position: relative;
      opacity: 0;
    }

    .slide-content li::before {
      content: '';
      position: absolute;
      left: 0;
      top: 10px;
      width: 12px;
      height: 12px;
      background: ${theme.accent};
      border-radius: 50%;
    }

    .slide.active .slide-content li {
      opacity: 1;
    }

    /* Element Animations */
    .elem-none .slide.active .slide-content li {
      opacity: 1;
    }

    .elem-fade-in .slide.active .slide-content li {
      animation: elemFadeIn ${duration}ms ease forwards;
    }

    .elem-slide-up .slide.active .slide-content li {
      animation: elemSlideUp ${duration}ms ease forwards;
    }

    .elem-slide-left .slide.active .slide-content li {
      animation: elemSlideLeft ${duration}ms ease forwards;
    }

    .elem-slide-right .slide.active .slide-content li {
      animation: elemSlideRight ${duration}ms ease forwards;
    }

    .elem-zoom-in .slide.active .slide-content li {
      animation: elemZoomIn ${duration}ms ease forwards;
    }

    .elem-bounce .slide.active .slide-content li {
      animation: elemBounce ${duration}ms ease forwards;
    }

    .elem-flip .slide.active .slide-content li {
      animation: elemFlip ${duration}ms ease forwards;
    }

    /* Staggered animation delays for list items */
    .slide.active .slide-content li:nth-child(1) { animation-delay: 0ms; }
    .slide.active .slide-content li:nth-child(2) { animation-delay: 100ms; }
    .slide.active .slide-content li:nth-child(3) { animation-delay: 200ms; }
    .slide.active .slide-content li:nth-child(4) { animation-delay: 300ms; }
    .slide.active .slide-content li:nth-child(5) { animation-delay: 400ms; }
    .slide.active .slide-content li:nth-child(6) { animation-delay: 500ms; }
    .slide.active .slide-content li:nth-child(7) { animation-delay: 600ms; }
    .slide.active .slide-content li:nth-child(8) { animation-delay: 700ms; }

    /* Text Animations for titles */
    .text-none .slide-title, .text-none .slide-subtitle {
      opacity: 1;
    }

    .text-typewriter .slide.active .slide-title {
      overflow: hidden;
      white-space: nowrap;
      animation: typewriter 2s steps(40) forwards, blink 0.75s step-end infinite;
      border-right: 3px solid ${theme.primary};
    }

    .text-fade-in-up .slide.active .slide-title,
    .text-fade-in-up .slide.active .slide-subtitle {
      animation: textFadeUp ${duration}ms ease forwards;
    }

    .text-fade-in-down .slide.active .slide-title,
    .text-fade-in-down .slide.active .slide-subtitle {
      animation: textFadeDown ${duration}ms ease forwards;
    }

    .text-bounce-in .slide.active .slide-title,
    .text-bounce-in .slide.active .slide-subtitle {
      animation: textBounceIn ${duration}ms cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
    }

    .text-zoom-in .slide.active .slide-title,
    .text-zoom-in .slide.active .slide-subtitle {
      animation: textZoomIn ${duration}ms ease forwards;
    }

    /* Initial states for text animations */
    .text-fade-in-up .slide-title,
    .text-fade-in-up .slide-subtitle,
    .text-fade-in-down .slide-title,
    .text-fade-in-down .slide-subtitle,
    .text-bounce-in .slide-title,
    .text-bounce-in .slide-subtitle,
    .text-zoom-in .slide-title,
    .text-zoom-in .slide-subtitle {
      opacity: 0;
    }

    .text-typewriter .slide-title {
      width: 0;
    }

    /* Keyframes for Element Animations */
    @keyframes elemFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes elemSlideUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes elemSlideLeft {
      from { opacity: 0; transform: translateX(50px); }
      to { opacity: 1; transform: translateX(0); }
    }

    @keyframes elemSlideRight {
      from { opacity: 0; transform: translateX(-50px); }
      to { opacity: 1; transform: translateX(0); }
    }

    @keyframes elemZoomIn {
      from { opacity: 0; transform: scale(0.5); }
      to { opacity: 1; transform: scale(1); }
    }

    @keyframes elemBounce {
      0% { opacity: 0; transform: translateY(30px); }
      50% { opacity: 1; transform: translateY(-10px); }
      70% { transform: translateY(5px); }
      100% { opacity: 1; transform: translateY(0); }
    }

    @keyframes elemFlip {
      from { opacity: 0; transform: perspective(400px) rotateX(-90deg); }
      to { opacity: 1; transform: perspective(400px) rotateX(0); }
    }

    /* Keyframes for Text Animations */
    @keyframes typewriter {
      from { width: 0; }
      to { width: 100%; }
    }

    @keyframes blink {
      50% { border-color: transparent; }
    }

    @keyframes textFadeUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes textFadeDown {
      from { opacity: 0; transform: translateY(-30px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes textBounceIn {
      0% { opacity: 0; transform: scale(0.3); }
      50% { transform: scale(1.1); }
      70% { transform: scale(0.9); }
      100% { opacity: 1; transform: scale(1); }
    }

    @keyframes textZoomIn {
      from { opacity: 0; transform: scale(0.5); }
      to { opacity: 1; transform: scale(1); }
    }

    .slide-notes {
      position: absolute;
      bottom: 30px;
      left: 80px;
      right: 80px;
      font-size: 0.875rem;
      color: ${theme.text}80;
      border-top: 1px solid ${theme.text}20;
      padding-top: 15px;
      display: none;
    }

    .show-notes .slide-notes {
      display: block;
    }

    .controls {
      position: fixed;
      bottom: 20px;
      right: 20px;
      display: flex;
      gap: 10px;
      z-index: 1000;
    }

    .controls button {
      width: 50px;
      height: 50px;
      border: none;
      border-radius: 50%;
      background: ${theme.primary};
      color: ${theme.text};
      cursor: pointer;
      font-size: 1.25rem;
      transition: transform 0.2s, background 0.2s;
    }

    .controls button:hover {
      transform: scale(1.1);
      background: ${theme.accent};
    }

    .progress {
      position: fixed;
      bottom: 0;
      left: 0;
      height: 4px;
      background: ${theme.accent};
      transition: width 0.3s ease;
    }

    .slide-counter {
      position: fixed;
      bottom: 30px;
      left: 30px;
      font-size: 0.875rem;
      color: ${theme.text}80;
    }

    /* Fullscreen styles */
    .fullscreen .controls { display: none; }
  </style>
</head>
<body>
  <div class="presentation transition-${slideTransition} elem-${elementAnimation} text-${textAnimation}" id="presentation">
    ${currentPresentation.slides.map((slide, index) => `
    <div class="slide${index === 0 ? ' active' : ''}" data-slide="${index}">
      <h1 class="slide-title">${slide.title}</h1>
      ${slide.subtitle ? `<h2 class="slide-subtitle">${slide.subtitle}</h2>` : ''}
      <div class="slide-content">
        <ul>
          ${slide.content.map(item => `<li>${item}</li>`).join('\n          ')}
        </ul>
      </div>
      ${slide.notes ? `<div class="slide-notes">${slide.notes}</div>` : ''}
    </div>
    `).join('')}
  </div>

  <div class="progress" id="progress"></div>
  <div class="slide-counter" id="counter">1 / ${currentPresentation.slides.length}</div>

  <div class="controls">
    <button onclick="prevSlide()" title="Previous (←)">◀</button>
    <button onclick="nextSlide()" title="Next (→)">▶</button>
    <button onclick="toggleNotes()" title="Toggle Notes (N)">📝</button>
    <button onclick="toggleFullscreen()" title="Fullscreen (F)">⛶</button>
  </div>

  <script>
    let currentSlide = 0;
    const slides = document.querySelectorAll('.slide');
    const totalSlides = slides.length;
    const progress = document.getElementById('progress');
    const counter = document.getElementById('counter');
    const presentation = document.getElementById('presentation');
    const transitionDuration = ${duration};

    function showSlide(index, direction = 'next') {
      const prevSlide = slides[currentSlide];
      const nextSlideEl = slides[index];

      // Add exit class to current slide
      if (prevSlide && prevSlide !== nextSlideEl) {
        prevSlide.classList.add('exit');
        prevSlide.classList.remove('active');

        // Remove exit class after transition
        setTimeout(() => {
          prevSlide.classList.remove('exit');
        }, transitionDuration);
      }

      // Activate new slide
      nextSlideEl.classList.add('active');

      currentSlide = index;
      progress.style.width = ((index + 1) / totalSlides * 100) + '%';
      counter.textContent = (index + 1) + ' / ' + totalSlides;
    }

    function nextSlide() {
      if (currentSlide < totalSlides - 1) {
        showSlide(currentSlide + 1, 'next');
      }
    }

    function prevSlide() {
      if (currentSlide > 0) {
        showSlide(currentSlide - 1, 'prev');
      }
    }

    function toggleNotes() {
      presentation.classList.toggle('show-notes');
    }

    function toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        presentation.classList.add('fullscreen');
      } else {
        document.exitFullscreen();
        presentation.classList.remove('fullscreen');
      }
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      switch(e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prevSlide();
          break;
        case 'n':
        case 'N':
          toggleNotes();
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'Home':
          showSlide(0);
          break;
        case 'End':
          showSlide(totalSlides - 1);
          break;
      }
    });

    // Touch/swipe support
    let touchStartX = 0;
    let touchEndX = 0;

    document.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    });

    document.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) nextSlide();
        else prevSlide();
      }
    });

    ${animations.autoAdvance ? `
    // Auto-advance slides
    setInterval(() => {
      if (currentSlide < totalSlides - 1) {
        nextSlide();
      }
    }, ${animations.autoAdvanceDelay * 1000});
    ` : ''}
  </script>
</body>
</html>`;

    return html;
  },

  reset: () => {
    set({
      currentPresentation: null,
      presentationId: null,
      isDirty: false,
      isLoading: false,
      isSaving: false,
      error: null,
      history: [],
      historyIndex: -1,
      selectedSlideIndex: 0,
      activePanel: null,
      isPreviewMode: false,
      isExporting: false,
      pendingAction: null,
    });
  },

  setError: (error) => {
    set({ error });
  },
}));
